import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, CreditCard, Banknote, Store, MapPin, Plus, Minus, CheckCircle2, Upload } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Loader } from "../components/Loader";
import { PageHeader } from "../components/PageHeader";
import { fileToCompressedDataUrl } from "../lib/image";

export default function Checkout() {
  const { productId } = useParams();
  const [sp] = useSearchParams();
  const color = sp.get("color") || "";
  const size = sp.get("size") || "";
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  const [product, setProduct] = useState(null);
  const [storeMethods, setStoreMethods] = useState([]);
  const [paymentChoice, setPaymentChoice] = useState("wallet");
  const [pickedStoreMethod, setPickedStoreMethod] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [qty, setQty] = useState(1);
  const [dName, setDName] = useState(user?.name || "");
  const [dPhone, setDPhone] = useState(user?.phone || "");
  const [cities, setCities] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [dCity, setDCity] = useState("");
  const [dRegion, setDRegion] = useState("");
  const [dAddress, setDAddress] = useState("");
  const [note, setNote] = useState("");
  const [buyerLat, setBuyerLat] = useState(null);
  const [buyerLng, setBuyerLng] = useState(null);
  const [distanceQuote, setDistanceQuote] = useState(null);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { refresh(); }, []); // eslint-disable-line
  useEffect(() => {
    if (!productId) return;
    (async () => {
      try {
        const p = await api(`/products/${productId}`);
        setProduct(p);
        try { setStoreMethods(await api(`/sellers/${p.seller_id}/payment-methods`) || []); } catch {}
        try { setCities(await api("/cities")); } catch {}
      } catch (e) { toast.error(e.message); }
    })();
  }, [productId]);

  const quantity = Math.max(1, qty);
  const discount = product?.discount_percent || 0;
  const unitPrice = product ? Number(product.price) * (1 - discount / 100) : 0;
  const total = unitPrice * quantity;
  const originalTotal = (product?.price || 0) * quantity;

  const deliveryInfo = useMemo(() => {
    if (!product) return { fee: 0, deliverable: true, needCity: false, needLocation: false, loading: false, label: "" };
    const dt = product.delivery_type;
    if (!dt) return { fee: 0, deliverable: true, needCity: false, needLocation: false, loading: false, label: "" };
    if (dt === "free") return { fee: 0, deliverable: true, needCity: false, needLocation: false, loading: false, label: "توصيل مجاني" };
    if (dt === "flat") return { fee: Number(product.delivery_flat_price || 0), deliverable: true, needCity: false, needLocation: false, loading: false, label: "" };
    if (dt === "distance") {
      if (buyerLat == null) return { fee: 0, deliverable: true, needLocation: true, needCity: false, loading: false, label: "" };
      if (quoteBusy || !distanceQuote) return { fee: 0, deliverable: true, needLocation: false, needCity: false, loading: true, label: "" };
      return { fee: Number(distanceQuote.fee || 0), deliverable: !!distanceQuote.deliverable, reason: distanceQuote.reason, needLocation: false, needCity: false, loading: false, label: distanceQuote.distance_km != null ? `المسافة ${distanceQuote.distance_km} كم` : "" };
    }
    if (!selectedCityId) return { fee: 0, deliverable: true, needCity: true, needLocation: false, loading: false, label: "" };
    const cp = (product.delivery_city_prices || []).find((x) => x.city_id === selectedCityId);
    if (!cp) return { fee: 0, deliverable: false, needCity: false, needLocation: false, loading: false, label: "" };
    return { fee: Number(cp.price || 0), deliverable: true, needCity: false, needLocation: false, loading: false, label: "" };
  }, [product, selectedCityId, buyerLat, buyerLng, distanceQuote, quoteBusy]);

  const grandTotal = total + (deliveryInfo.deliverable ? deliveryInfo.fee : 0);
  const walletBal = Number(user?.wallet_balance || 0);
  const insufficient = paymentChoice === "wallet" && walletBal < grandTotal;

  const captureLocation = () => {
    if (!navigator.geolocation) return toast.error("المتصفح لا يدعم تحديد الموقع");
    setQuoteBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setBuyerLat(pos.coords.latitude); setBuyerLng(pos.coords.longitude); },
      () => { setQuoteBusy(false); toast.error("تعذّر تحديد الموقع — فعّل إذن الموقع في المتصفح"); },
      { enableHighAccuracy: true }
    );
  };

  useEffect(() => {
    if (!product || product.delivery_type !== "distance" || buyerLat == null) return;
    let cancelled = false;
    (async () => {
      setQuoteBusy(true);
      try {
        const q = await api("/delivery/quote", { method: "POST", body: JSON.stringify({ product_id: product.product_id, delivery_city_id: selectedCityId || undefined, delivery_lat: buyerLat, delivery_lng: buyerLng }) });
        if (!cancelled) setDistanceQuote(q);
      } catch (e) { if (!cancelled) toast.error(e.message); }
      finally { if (!cancelled) setQuoteBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [product, buyerLat, buyerLng, selectedCityId]);

  const onReceipt = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { setReceipt(await fileToCompressedDataUrl(f, 1080, 0.5)); } catch { toast.error("فشل رفع الإيصال"); }
  };

  const validate = () => {
    if (!dName.trim()) return "الاسم مطلوب";
    if (!dPhone.trim() || dPhone.trim().length < 7) return "رقم الهاتف مطلوب";
    if (!selectedCityId) return "اختر المدينة";
    if (!dRegion.trim()) return "المنطقة مطلوبة";
    if (product?.delivery_type === "distance" && buyerLat == null) return "حدّد موقع التوصيل";
    return null;
  };

  const submit = async () => {
    if (!product) return;
    const err = validate();
    if (err) return toast.error(err);
    if (quantity > (product.quantity || 0)) return toast.error("الكمية غير متاحة");
    if (!deliveryInfo.deliverable) return toast.error(deliveryInfo.reason === "out_of_range" ? "أنت خارج نطاق التوصيل (الحد الأقصى 60 كم)" : "التوصيل غير متاح لمدينتك");

    if (paymentChoice === "tlync") {
      setBusy(true);
      try {
        const res = await api("/payments/tlync/initiate", { method: "POST", body: JSON.stringify({
          purpose: "order", phone: dPhone.trim(), email: user?.email || "", product_id: productId, quantity,
          delivery_address: `${dRegion} - ${dCity}${dAddress ? " - " + dAddress : ""}`, delivery_name: dName.trim(),
          delivery_phone: dPhone.trim(), delivery_city: dCity.trim(), delivery_city_id: selectedCityId || "",
          delivery_region: dRegion.trim(), delivery_lat: buyerLat ?? undefined, delivery_lng: buyerLng ?? undefined,
          color: color || undefined, size: size || undefined,
        }) });
        if (res.redirect_url) window.location.href = res.redirect_url;
      } catch (e) { toast.error(e.message); } finally { setBusy(false); }
      return;
    }

    let pmId = "wallet";
    if (paymentChoice === "store") {
      if (!pickedStoreMethod) return toast.error("اختر طريقة الدفع");
      if (!receipt) return toast.error("يجب رفع إيصال التحويل");
      pmId = `store:${pickedStoreMethod.name}`;
    } else if (paymentChoice === "cod") pmId = "cod";
    else if (insufficient) return toast.error("الرصيد غير كافٍ — اشحن محفظتك أولاً");

    setBusy(true);
    try {
      await api("/orders", { method: "POST", body: JSON.stringify({
        product_id: productId, quantity, payment_method_id: pmId, receipt_base64: receipt, buyer_note: note.trim() || undefined,
        delivery_name: dName.trim(), delivery_phone: dPhone.trim(), delivery_city: dCity.trim(), delivery_city_id: selectedCityId || undefined,
        delivery_region: dRegion.trim(), delivery_address: dAddress.trim() || undefined, delivery_street: dAddress.trim() || undefined,
        delivery_lat: buyerLat ?? undefined, delivery_lng: buyerLng ?? undefined, color: color || undefined, size: size || undefined,
      }) });
      toast.success(paymentChoice === "wallet" ? `تم خصم ${grandTotal.toFixed(2)} د.ل من محفظتك` : "تم إرسال الطلب");
      refresh();
      navigate("/orders");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (!product) return <Loader />;

  return (
    <div className="animate-fade-in-up max-w-2xl mx-auto">
      <PageHeader title="إتمام الشراء" />
      {/* Product summary */}
      <div className="flex gap-3 p-3 bg-white rounded-2xl border border-[#E8EAF2]">
        <div className="w-20 h-20 rounded-xl bg-[#F7F8FE] overflow-hidden shrink-0">{product.image_base64 && <img src={product.image_base64} alt="" className="w-full h-full object-cover" />}</div>
        <div className="flex-1">
          <p className="font-black text-[#161629]">{product.name}</p>
          {(color || size) && <p className="text-xs text-[#6B7280] mt-1">{color ? `لون: ${color}` : ""}{color && size ? " • " : ""}{size ? `حجم: ${size}` : ""}</p>}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-lg font-black text-[#FF3D5A]">{unitPrice.toFixed(2)} د.ل</span>
            {discount > 0 && <span className="text-sm text-[#9CA3AF] line-through">{Number(product.price).toFixed(2)}</span>}
          </div>
        </div>
      </div>

      {/* Quantity */}
      <Label>الكمية</Label>
      <div className="flex items-center gap-3">
        <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, quantity - 1))} className="w-11 h-11 rounded-xl bg-white border border-[#E8EAF2] flex items-center justify-center"><Minus size={18} /></button>
        <span className="w-16 h-11 rounded-xl bg-white border border-[#E8EAF2] flex items-center justify-center font-black text-lg">{quantity}</span>
        <button data-testid="qty-plus" onClick={() => setQty(Math.min(product.quantity, quantity + 1))} className="w-11 h-11 rounded-xl bg-white border border-[#E8EAF2] flex items-center justify-center"><Plus size={18} /></button>
        <span className="text-sm text-[#6B7280] ms-auto">متوفر: {product.quantity}</span>
      </div>

      {/* Delivery */}
      <h2 className="text-base font-black text-[#161629] mt-6 mb-1">📦 معلومات التوصيل</h2>
      <Label>الاسم الكامل *</Label>
      <Input testId="d-name" value={dName} onChange={setDName} placeholder="اسمك" />
      <Label>رقم الهاتف *</Label>
      <Input testId="d-phone" value={dPhone} onChange={setDPhone} placeholder="091XXXXXXX" />
      <Label>المدينة *</Label>
      <div className="flex gap-2 flex-wrap">
        {cities.map((c) => {
          const sel = selectedCityId === c.city_id;
          return <button key={c.city_id} data-testid={`checkout-city-${c.city_id}`} onClick={() => { setSelectedCityId(c.city_id); setDCity(c.name); }} className={`px-4 py-2 rounded-xl border font-bold text-sm ${sel ? "bg-[#FF3D5A] border-[#FF3D5A] text-white" : "bg-white border-[#E8EAF2] text-[#161629]"}`}>{c.name}</button>;
        })}
      </div>

      {product.delivery_type === "distance" && (
        <div className="mt-3">
          <button data-testid="capture-buyer-location" onClick={captureLocation} disabled={quoteBusy} className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-[#FF3D5A] text-white font-black">
            <MapPin size={18} /> {buyerLat != null ? "تم تحديد موقعك — اضغط للتحديث" : "حدّد موقع التوصيل 📍"}
          </button>
          {deliveryInfo.loading && <p className="text-sm text-[#6B7280] mt-2">جارٍ حساب أجرة التوصيل...</p>}
          {!deliveryInfo.loading && deliveryInfo.deliverable && buyerLat != null && <p className="text-sm text-[#00D26A] mt-2">{deliveryInfo.label ? `${deliveryInfo.label} — ` : ""}أجرة التوصيل: {deliveryInfo.fee.toFixed(2)} د.ل</p>}
          {!deliveryInfo.loading && !deliveryInfo.deliverable && buyerLat != null && <p className="text-sm text-[#FF3D5A] mt-2" data-testid="distance-unavailable">أنت خارج نطاق التوصيل (الحد الأقصى 60 كم)</p>}
        </div>
      )}
      {deliveryInfo.needCity && <p className="text-sm text-[#6B7280] mt-2">اختر مدينتك لعرض أجرة التوصيل</p>}
      {product.delivery_type !== "distance" && !deliveryInfo.deliverable && <p className="text-sm text-[#FF3D5A] mt-2" data-testid="delivery-unavailable">التوصيل غير متاح لمدينتك</p>}
      {product.delivery_type !== "distance" && deliveryInfo.deliverable && !deliveryInfo.needCity && product.delivery_type && <p className="text-sm text-[#00D26A] mt-2">{deliveryInfo.fee > 0 ? `أجرة التوصيل: ${deliveryInfo.fee.toFixed(2)} د.ل` : (deliveryInfo.label || "توصيل مجاني")}</p>}

      <Label>المنطقة *</Label>
      <Input testId="d-region" value={dRegion} onChange={setDRegion} placeholder="مثلاً: حي الأندلس" />
      <Label>تفاصيل العنوان (اختياري)</Label>
      <Input testId="d-address" value={dAddress} onChange={setDAddress} placeholder="رقم البناية، الشارع، علامة مميزة" />

      {/* Payment */}
      <h2 className="text-base font-black text-[#161629] mt-6 mb-2">💳 طريقة الدفع</h2>
      <PayOption testId="pay-wallet" active={paymentChoice === "wallet"} onClick={() => setPaymentChoice("wallet")} icon={Wallet} color="#00D26A" title="المحفظة" desc={insufficient ? `الرصيد غير كافٍ (${walletBal.toFixed(2)} د.ل)` : `الرصيد: ${walletBal.toFixed(2)} د.ل — خصم فوري`} danger={insufficient} />
      <PayOption testId="pay-tlync" active={paymentChoice === "tlync"} onClick={() => setPaymentChoice("tlync")} icon={CreditCard} color="#3F8CFF" title="T-Lync (بطاقة / موبي كاش / سداد)" desc="دفع مباشر آمن عبر بوابة الدفع الليبية" />
      <PayOption testId="pay-cod" active={paymentChoice === "cod"} onClick={() => setPaymentChoice("cod")} icon={Banknote} color="#16a34a" title="نقداً عند الاستلام 💵" desc="ادفع نقداً عند استلام المنتج" />
      {storeMethods.length > 0 && <PayOption testId="pay-store" active={paymentChoice === "store"} onClick={() => setPaymentChoice("store")} icon={Store} color="#FF8A3C" title="طرق دفع المتجر" desc={`${storeMethods.length} طريقة — تحويل يدوي + إيصال`} />}

      {paymentChoice === "store" && (
        <div className="p-3 bg-[#F7F8FE] rounded-2xl mt-2 space-y-2">
          {storeMethods.map((m) => {
            const sel = pickedStoreMethod?.name === m.name;
            return (
              <button key={m.name} data-testid={`store-method-${m.name}`} onClick={() => setPickedStoreMethod(m)} className={`w-full flex items-center gap-2 p-3 rounded-xl border-2 text-start ${sel ? "border-[#FF3D5A] bg-[#FFE5EA]" : "border-[#E8EAF2] bg-white"}`}>
                <div className="flex-1"><p className="font-black text-sm text-[#161629]">{m.name}</p>{m.details && <p className="text-xs text-[#6B7280]">{m.details}</p>}</div>
                {sel && <CheckCircle2 size={20} className="text-[#FF3D5A]" />}
              </button>
            );
          })}
          {pickedStoreMethod && (
            <div>
              <Label>إيصال التحويل *</Label>
              <label className="flex flex-col items-center justify-center h-40 rounded-2xl border-2 border-dashed border-[#FF3D5A] bg-[#FFE5EA] cursor-pointer overflow-hidden" data-testid="upload-receipt">
                {receipt ? <img src={receipt} alt="" className="w-full h-full object-cover" /> : <><Upload size={32} className="text-[#FF3D5A]" /><span className="text-sm text-[#FF3D5A] font-bold mt-2">اضغط لرفع صورة الإيصال</span></>}
                <input type="file" accept="image/*" className="hidden" onChange={onReceipt} />
              </label>
            </div>
          )}
        </div>
      )}

      <Label>ملاحظات للبائع (اختياري)</Label>
      <textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="مثال: اتصل قبل التوصيل" className="w-full bg-white rounded-xl px-4 py-3 border border-[#E8EAF2] outline-none focus:ring-2 focus:ring-[#FF3D5A] min-h-[70px]" />

      {/* Summary */}
      <div className="mt-5 p-4 bg-white rounded-2xl border border-[#E8EAF2]">
        <p className="font-black text-[#161629] mb-2">ملخص الطلب</p>
        <Row k={`السعر × ${quantity}`} v={`${originalTotal.toFixed(2)} د.ل`} />
        {discount > 0 && <Row k={`الخصم (${discount}%)`} v={`-${(originalTotal - total).toFixed(2)} د.ل`} green />}
        <Row k="التوصيل" v={deliveryInfo.needLocation ? "حدّد الموقع" : deliveryInfo.loading ? "جارٍ الحساب..." : !deliveryInfo.deliverable ? "غير متاح" : deliveryInfo.needCity ? "اختر المدينة" : deliveryInfo.fee > 0 ? `${deliveryInfo.fee.toFixed(2)} د.ل` : "مجاني"} />
        <div className="flex justify-between pt-3 mt-2 border-t-2 border-[#E8EAF2]"><span className="font-black text-[#161629]">الإجمالي</span><span className="text-xl font-black text-[#FF3D5A]">{grandTotal.toFixed(2)} د.ل</span></div>
      </div>

      <button data-testid="checkout-submit" disabled={busy || deliveryInfo.needLocation || deliveryInfo.loading || !deliveryInfo.deliverable} onClick={submit} className="w-full h-14 rounded-2xl bg-[#FF3D5A] text-white font-black mt-5 disabled:opacity-60">
        {busy ? "..." : paymentChoice === "wallet" ? `ادفع من المحفظة (${grandTotal.toFixed(2)} د.ل)` : paymentChoice === "tlync" ? `ادفع عبر T-Lync (${grandTotal.toFixed(2)} د.ل)` : paymentChoice === "cod" ? `إرسال الطلب — نقداً (${grandTotal.toFixed(2)} د.ل)` : `إرسال الطلب (${grandTotal.toFixed(2)} د.ل)`}
      </button>
      {insufficient && paymentChoice === "wallet" && <button data-testid="topup-link" onClick={() => navigate("/wallet")} className="w-full text-center text-[#FF3D5A] font-black mt-3">+ شحن المحفظة</button>}
    </div>
  );
}

function Label({ children }) { return <p className="text-sm font-bold text-[#161629] mt-4 mb-1.5">{children}</p>; }
function Input({ value, onChange, placeholder, testId }) {
  return <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-white rounded-xl px-4 py-3 border border-[#E8EAF2] outline-none focus:ring-2 focus:ring-[#FF3D5A]" />;
}
function Row({ k, v, green }) { return <div className="flex justify-between py-1.5"><span className={`text-sm font-bold ${green ? "text-[#00D26A]" : "text-[#6B7280]"}`}>{k}</span><span className={`text-sm font-black ${green ? "text-[#00D26A]" : "text-[#161629]"}`}>{v}</span></div>; }
function PayOption({ active, onClick, icon: Icon, color, title, desc, testId, danger }) {
  return (
    <button data-testid={testId} onClick={onClick} className={`w-full flex items-center gap-3 p-4 rounded-2xl border-2 mt-2 text-start ${active ? "border-[#FF3D5A] bg-[#FFE5EA]" : "border-[#E8EAF2] bg-white"}`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0" style={{ backgroundColor: color }}><Icon size={22} /></div>
      <div className="flex-1"><p className="font-black text-[#161629]">{title}</p><p className={`text-xs mt-0.5 ${danger ? "text-[#FF3D5A] font-black" : "text-[#6B7280]"}`}>{desc}</p></div>
      {active && <CheckCircle2 size={22} className="text-[#FF3D5A]" />}
    </button>
  );
}
